import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/App";
import LeadContactSections from "@/pages/LeadContactSections";
import { realLeadToFocusShape } from "@/lib/leadRealToFocusShape";
import { prefillLeadInformation } from "@/lib/leadInformationPrefill";

// Collections the dialer keeps in its own normalized shape — never pushed back into the queue lead.
const LOCAL_ONLY_KEYS = new Set(["contacts", "addresses", "locations"]);
const toFocus = (raw) => prefillLeadInformation(realLeadToFocusShape(raw));

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Sales Dialer Quick edit — the lead's full Lead Focus → Information tab, saving each field instantly. */
export default function QuickEditLeadFields({ lead, onLeadPatched }) {
  const { token, user } = useAuth();
  const [record, setRecord] = useState(null);
  const leadId = lead?.id;

  useEffect(() => {
    setRecord(null);
    if (!leadId || !token) return undefined;
    let alive = true;
    axios.get(`${API}/leads/${encodeURIComponent(leadId)}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => alive && setRecord(toFocus(r.data || lead)))
      .catch(() => { if (alive) { toast.error("Couldn't load the latest lead details — showing the dialer copy"); setRecord(toFocus(lead)); } });
    return () => { alive = false; };
  }, [leadId, token]); // eslint-disable-line react-hooks/exhaustive-deps -- refetch only when the lead changes

  const onFieldUpdate = async (key, value) => {
    const prev = record;
    setRecord(r => ({ ...r, [key]: value }));
    try {
      await axios.put(`${API}/leads/${encodeURIComponent(leadId)}`, { [key]: value },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 10000 });
      if (!LOCAL_ONLY_KEYS.has(key)) onLeadPatched?.({ [key]: value });
    } catch (e) {
      setRecord(prev);
      const d = e?.response?.data?.detail;
      toast.error(`Couldn't save ${key}: ${typeof d === "string" ? d : "please retry"}`);
    }
  };

  if (!record) {
    return <div data-testid="qe-loading" style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#888", padding: 12 }}><Loader2 className="animate-spin" style={{ width: 12, height: 12 }} /> Loading lead information…</div>;
  }
  return (
    <div data-testid="qe-lead-fields">
      <LeadContactSections lead={record} userRole={user?.role || "sales_manager"} currentUser={user} compact onFieldUpdate={onFieldUpdate} />
    </div>
  );
}
